/**
 * This is a patch migration from 1.0.1 to 1.0.2.
 *
 * - Adds voice-over metadata to auxiliary audio layer audio assets.
 */

import { cloneDeep } from 'lodash';
import { Migration, VideoDescriptor_v1_0_1, VideoDescriptor_v1_0_2 } from '../types';
import { v1_0_2 } from '@libs/waymark-video/video-descriptor-types';

export type MigrationContext = {
  auxiliaryVoiceOverMetadata?: {
    speakerGUID: string;
    scriptText: string;
    timing: v1_0_2.VoiceOverTimingWord[];
  };
};

const implementation = (
  inputVideoDescriptor: VideoDescriptor_v1_0_1,
  context?: MigrationContext,
): VideoDescriptor_v1_0_2 => {
  const newVideoDescriptor: VideoDescriptor_v1_0_2 = {
    ...(cloneDeep(inputVideoDescriptor) as unknown as VideoDescriptor_v1_0_2),
    version: '1.0.2',
  };

  if (!context?.auxiliaryVoiceOverMetadata) {
    return newVideoDescriptor;
  }

  const backgroundAudioLayerUUID = newVideoDescriptor.templateManifest.__backgroundAudioLayerUUID;
  const auxiliaryAudioLayer = newVideoDescriptor.projectManifest.layers.find(
    (layer): layer is v1_0_2.WaymarkAudioLayer =>
      layer.ty === v1_0_2.LayerType.WaymarkAudio && layer.meta.uuid !== backgroundAudioLayerUUID,
  );

  if (auxiliaryAudioLayer) {
    const audioAssetID = auxiliaryAudioLayer.refId;
    const audioAsset = newVideoDescriptor.projectManifest.assets.find(
      (asset): asset is v1_0_2.ProjectManifestAudioAsset => {
        return (
          asset.id === audioAssetID && 'type' in asset && asset.type === v1_0_2.AssetType.Audio
        );
      },
    );

    if (audioAsset) {
      audioAsset.meta = {
        voiceOver: {
          speakerGUID: context.auxiliaryVoiceOverMetadata.speakerGUID,
          scriptText: context.auxiliaryVoiceOverMetadata.scriptText,
          timing: context.auxiliaryVoiceOverMetadata.timing,
        },
      };
    } else {
      console.warn(
        `Audio asset with ID ${audioAssetID} not found in project manifest assets. Skipping voice-over metadata addition.`,
      );
    }
  }

  return newVideoDescriptor;
};

const migration: Migration<VideoDescriptor_v1_0_1, VideoDescriptor_v1_0_2> = {
  inputVersion: '1.0.1',
  targetVersion: '1.0.2',
  description: 'Migrates from 1.0.1 to 1.0.2. See migration file for details.',
  name: 'v1_0_1_migration',
  implementation,
};

export default migration;
